import { Link } from 'react-router-dom';
import { Globe, Mail } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

/**
 * AppFooter — minimalist, official footer used across the platform.
 * - Light / Dark aware (tailwind dark: variants).
 * - Three modes: 'full' (with quick links + company info), 'mini' (just legal line),
 *   and 'dashboard' (compact, single line for inside the app shell).
 *
 * Always carries the official © 2026 Trust Ed Techno line + a small link
 * to www.TrustEdTechno.com.
 */
const AppFooter = ({ language: _legacyLang, variant = 'full' }) => {
  const { language, localize } = useLanguage();
  const isAr = language === 'ar';
  const t = (ar, en) => localize({ ar, en });

  /* ──────────────── Mini / Dashboard variant ──────────────── */
  if (variant === 'mini' || variant === 'dashboard') {
    const isDash = variant === 'dashboard';
    return (
      <footer
        className={`${isDash ? 'mt-auto' : ''} border-t border-slate-200 dark:border-white/5 bg-white/60 dark:bg-[#0d1117]/80 backdrop-blur-sm`}
        data-testid="app-footer-mini"
        dir={isAr ? 'rtl' : 'ltr'}
      >
        <div className={`max-w-6xl mx-auto px-6 ${isDash ? 'py-3' : 'py-5'} space-y-2`}>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2 text-center sm:text-start">
            <p className="text-[11px] sm:text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
              {t(
                '© 2026 ترست اي دي تكنو للتطوير والابتكار ذ.م.م. جميع الحقوق محفوظة.',
                '© 2026 Trust Ed Techno for Development and Innovation L.L.C. All rights reserved.'
              )}
            </p>
            <a
              href="https://www.trustedtechno.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[11px] sm:text-xs font-medium text-slate-500 hover:text-indigo-600 dark:text-slate-400 dark:hover:text-indigo-300 inline-flex items-center gap-1 transition-colors"
              data-testid="footer-trustedtechno-link"
            >
              <Globe className="w-3 h-3" />
              www.TrustEdTechno.com
            </a>
          </div>
          {/* Beta disclaimer — even smaller for the mini variant */}
          <p
            className="text-[9px] sm:text-[10px] italic text-slate-400 dark:text-slate-500 leading-relaxed text-center"
            data-testid="footer-beta-disclaimer-mini"
          >
            <span className="not-italic font-bold">
              {t('إخلاء مسؤولية:', 'Disclaimer:')}
            </span>{' '}
            {t(
              'منصة في مرحلة Beta — جميع ملفات المدرسين والصور والأسماء تم إنشاؤها بالذكاء الاصطناعي لأغراض العرض. الإطلاق التجاري يخضع للموافقات التنظيمية في الإمارات.',
              'Beta/Demo platform — all tutor profiles, images, and names are AI-generated for demonstration. Commercial launch subject to UAE regulatory approvals.'
            )}
          </p>
        </div>
      </footer>
    );
  }

  /* ──────────────── Full variant ──────────────── */
  return (
    <footer
      className="bg-slate-900 text-white border-t border-white/5"
      data-testid="app-footer-full"
      dir={isAr ? 'rtl' : 'ltr'}
    >
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid md:grid-cols-3 gap-10 mb-10">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <img src="/lwt-icon-new.png" alt="LWT" className="h-8 w-8 brightness-0 invert" loading="lazy" />
              <div>
                <span className="brand-wordmark text-sm block">Learn With Trust</span>
                <span className="text-[10px] text-slate-400 block">Trust Ed Techno for Development and Innovation L.L.C.</span>
              </div>
            </div>
            <p className="text-slate-400 text-xs leading-relaxed max-w-xs">
              {t(
                'منصة تقنية تربط الطلاب بمدرسين مستقلين في الإمارات العربية المتحدة.',
                'A technology platform connecting students with independent tutors across the UAE.'
              )}
            </p>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-bold text-xs mb-4 text-slate-300 tracking-wide uppercase">
              {t('تواصل معنا', 'Contact Us')}
            </h4>
            <div className="space-y-2 text-slate-400 text-xs">
              <p>{t('أبوظبي، الإمارات العربية المتحدة', 'Abu Dhabi, United Arab Emirates')}</p>
              <a href="mailto:info@TrustEdTechno.com" className="flex items-center gap-1.5 hover:text-white transition-colors" data-testid="footer-support-email">
                <Mail className="w-3 h-3" />
                info@TrustEdTechno.com
              </a>
              <a
                href="https://www.trustedtechno.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 hover:text-white transition-colors"
                data-testid="footer-website-link"
              >
                <Globe className="w-3 h-3" />
                www.TrustEdTechno.com
              </a>
            </div>
          </div>

          {/* Quick links */}
          <div>
            <h4 className="font-bold text-xs mb-4 text-slate-300 tracking-wide uppercase">
              {t('روابط سريعة', 'Quick Links')}
            </h4>
            <div className="space-y-2 text-slate-400 text-xs">
              <Link to="/about" className="block hover:text-white transition-colors">
                {t('من نحن', 'About Us')}
              </Link>
              <Link to="/docs" className="block hover:text-white transition-colors" data-testid="footer-docs-link">
                {t('وثائق المشروع', 'Project Documentation')}
              </Link>
              <Link to="/contact" className="block hover:text-white transition-colors">
                {t('تواصل معنا', 'Contact Us')}
              </Link>
              <Link to="/terms" className="block hover:text-white transition-colors">
                {t('الشروط والأحكام', 'Terms & Conditions')}
              </Link>
              <Link to="/tutors" className="block hover:text-white transition-colors">
                {t('تصفح المدرسين', 'Browse Tutors')}
              </Link>
              <Link to="/accessibility" className="block hover:text-white transition-colors" data-testid="footer-a11y-link">
                <span className="inline-flex items-center gap-1.5">
                  <span aria-hidden>♿</span>
                  {t('إمكانية الوصول', 'Accessibility')}
                </span>
              </Link>
              <Link to="/parent-responsibilities" className="block hover:text-white transition-colors" data-testid="footer-parent-link">
                <span className="inline-flex items-center gap-1.5">
                  <span aria-hidden>👨‍👩‍👧</span>
                  {t('مسؤوليات ولي الأمر', 'Parent Responsibilities')}
                </span>
              </Link>
            </div>
          </div>
        </div>

        {/* Bottom legal */}
        <div className="border-t border-white/10 pt-6 text-center space-y-3">
          <p className="text-slate-500 text-[11px] max-w-2xl mx-auto leading-relaxed">
            {t(
              'Learn With Trust هي أداة تيسيرية (Facilitation Tool) تساعد أولياء الأمور على ترتيب جلسات تعليمية خاصّة مع معلمين مستقلين. مسؤولية إنشاء الحسابات واختيار المحتوى تقع بالكامل على ولي الأمر. المنصة لا تقدّم خدمات تعليمية بشكل مباشر.',
              'Learn With Trust is a Facilitation Tool that helps parents arrange private educational sessions with independent tutors. Account creation and content selection are the sole responsibility of the parent. The platform does not provide educational services directly.'
            )}
          </p>
          <p className="text-slate-400 text-xs font-medium">
            {t(
              '© 2026 ترست اي دي تكنو للتطوير والابتكار ذ.م.م. جميع الحقوق محفوظة.',
              '© 2026 Trust Ed Techno for Development and Innovation L.L.C. All rights reserved.'
            )}
          </p>

          {/* Regulatory & launch-date disclaimer (Sept 2026) */}
          <div className="pt-3 mt-1 border-t border-white/5">
            <p
              className="text-slate-500/90 text-[10px] sm:text-[11px] italic max-w-3xl mx-auto leading-relaxed"
              data-testid="footer-beta-disclaimer"
            >
              <span className="not-italic font-bold text-slate-400">
                {t('إخلاء مسؤولية:', 'Disclaimer:')}
              </span>{' '}
              {t(
                'الإطلاق التجاري الرسمي مُحدَّد بتاريخ 26 سبتمبر 2026، ويخضع للموافقات التنظيمية المحلية في دولة الإمارات العربية المتحدة. تتقيّد المنصة بأعلى معايير السلامة والامتثال، وتعمل بوصفها أداة تيسيرية بين أولياء الأمور والمعلمين المستقلين، وليست مقدّم خدمة تعليمية مباشرة.',
                'Official commercial launch is set for September 26, 2026, subject to local regulatory approvals in the UAE. The platform adheres to the highest safety and compliance standards and operates as a facilitation tool between parents and independent tutors — not as a direct educational provider.'
              )}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default AppFooter;
